/* eslint-disable object-curly-newline */
import {Injectable} from '@angular/core';
import {v4 as uuidv4} from 'uuid';
import {HttpService} from './http.service';
import {LocalStorageService} from './local-storage.service';
import {Subject, throwError} from 'rxjs';
import {catchError, retry, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorVideoAction} from '../stores/root-store/actions/video.action';
import {ErrorEnum} from '../../../enums/error.enum';
import {Store} from '@ngrx/store';
import VideoState from '../stores/root-store/video.state';

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    public sendEvents = true;
    public sessionUpdated: Subject<boolean> = new Subject<boolean>();

    private _sessionToken: string;
    private _appInstanceToken: string;
    private _videoId: string;
    private _uid: string;
    private _isInitialized = false;
    private _originUrl: string;
    private _publishTimestamp?: string;
    private _preview = false;

    private _data: Map<string, any> = new Map();

    constructor(
        private httpService: HttpService,
        private localStorageService: LocalStorageService,
        private store: Store<{video: VideoState}>
    ) {}

    initialize(videoId: string, uid?: string, originUrl?: string, publishTimestamp?: string, preview?: boolean) {
        this._appInstanceToken = uuidv4();
        this._videoId = videoId;
        this._uid = uid;
        this._originUrl = originUrl;
        this._publishTimestamp = publishTimestamp;
        this._preview = preview;
    }

    get videoId() {
        return this._videoId;
    }

    get uid() {
        return this._uid;
    }

    get appInstanceToken() {
        return this._appInstanceToken;
    }

    get sessionToken() {
        return this._sessionToken;
    }

    get data() {
        return this._data;
    }

    getLastSession() {
        return this._sessionToken || this.localStorageService.getItem(this.getSessionKey());
    }

    async save({isInitalSave} = {isInitalSave: false}): Promise<{data: Map<string, any>; sessionToken: string}> {
        if (this._preview) {
            return;
        }

        return await this.httpService
            .session(this._videoId, this.getLastSession(), JSON.stringify(this.getPayload()))
            .pipe(
                catchError((error: any) => {
                    if ((error as HttpErrorResponse).status === 502) {
                        // don't show error message if error occure during playing video
                        if (isInitalSave) {
                            this.store.dispatch(
                                ErrorVideoAction({
                                    errorType: ErrorEnum.HIGH_TRAFFIC_ERROR,
                                    error
                                })
                            );
                        }

                        return throwError(() => new Error(error));
                    }
                }),
                retry({
                    delay: 5000,
                    count: 2
                }),
                tap((newSessionToken) => {
                    this._sessionToken = newSessionToken.sessionToken;
                    this._data = new Map(newSessionToken.data);
                    this._isInitialized = true;

                    this.localStorageService.setItem(this.getSessionKey(), this._sessionToken);
                })
            )
            .toPromise();
    }

    private getPayload() {
        return {
            type: this._isInitialized ? 'update' : 'init',
            instance: this._appInstanceToken,
            uid: this._uid,
            data: [...this._data],
            videoPlayerVersion: (window as any).videoPlayerVersion,
            originUrl: this._originUrl,
            publishTimestamp: this._publishTimestamp,
            preview: this._preview
        };
    }

    private getSessionKey() {
        return 'session:' + this._videoId;
    }
}
